/**
 * Hero section for the Home page
 * Purpose: Present the primary value proposition with a photographic background,
 * a subtle logo watermark, and clear CTAs.
 *
 * This version accepts an optional subtitle prop. If subtitle is provided,
 * it is rendered verbatim to guarantee exact copy (no rewriting or automatic corrections).
 */

import React from 'react'
import { Link } from 'react-router'

/**
 * Props for the Hero component
 *
 * Interface-level: Defines props accepted by the Hero component.
 */
export interface HeroProps {
  /** Optional supporting text under the headline, rendered verbatim when provided */
  subtitle?: string
  /** Background photo URL; falls back to a tasteful placeholder */
  bgImageUrl?: string
  /** Watermark logo URL; shown when showWatermark is true */
  logoUrl?: string
  /** Toggle the watermark visibility */
  showWatermark?: boolean
}

/**
 * Hero
 * Displays the main marketing message with a background image, watermark, and actions.
 *
 * Function-level: Renders subtitle prop verbatim when present; otherwise falls back to a default text.
 */
export default function Hero({
  subtitle,
  bgImageUrl = 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/112fddec-2cf3-4409-9dd3-8f06e1850bb7.jpg',
  logoUrl,
  showWatermark = true,
}: HeroProps): JSX.Element {
  return (
    <section className="relative overflow-hidden" aria-label="Hero">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={bgImageUrl}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover"
        />
        {/* Contrast overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-[#3a160e]/55 to-black/40" />
        {/* Soft vignette for depth */}
        <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_20%_10%,rgba(255,255,255,0.08),transparent)] pointer-events-none" />
      </div>

      {/* Optional watermark (Edicoo logo) */}
      {showWatermark && logoUrl && (
        <div className="pointer-events-none absolute inset-0 flex items-start justify-center">
          <img
            src={logoUrl}
            alt=""
            aria-hidden="true"
            className="mt-6 w-[640px] max-w-[85%] opacity-10 object-contain"
          />
        </div>
      )}

      {/* Content */}
      <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        <div className="py-20 sm:py-24 lg:py-28">
          <h1 className="font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-tight">
            <span className="block">Edicoo – Iraq's Leading</span>
            <span className="block">Digital, Influencer Marketing</span>
            <span className="block">
              & <span className="text-orange-300">Media Buying Agency</span>
            </span>
          </h1>

          {/*
            Render subtitle verbatim when provided.
            When subtitle is undefined/null/empty string, render the default marketing sentence.
          */}
          {subtitle ? (
            <p className="mt-5 max-w-2xl text-base sm:text-lg text-orange-100/90">
              {subtitle}
            </p>
          ) : (
            <p className="mt-5 max-w-2xl text-base sm:text-lg text-orange-100/90">
              Boost your brand across Iraq with Edicoo. We deliver expert digital marketing, influencer campaigns, and TV &amp; media planning services trusted by global and local brands for over 15 years.
            </p>
          )}

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/services"
              className="inline-flex items-center justify-center rounded-md bg-orange-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-orange-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2"
            >
              Explore Services
            </Link>

            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-md bg-white/10 px-5 py-3 text-sm font-semibold text-white backdrop-blur hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}